import get from 'lodash-es/get'
import React from 'react'

import type { UploadProps } from '@fourthwall/components'
import {
  InputUI,
  Loading,
  Subsection,
  Toasts,
  Upload,
  useMediaQueryContext,
} from '@fourthwall/components'
import { UploadIcon } from '@fourthwall/icons'
import { useFormikContext } from '@fourthwall/utils/dist/formik'
import { getRejectionErrors } from '@utils/dropzone'

import { RemovableUpload } from '../../../../components/RemovableUpload/RemovableUpload'
import { UploadedImage } from '../../../../components/UploadedImage/UploadedImage'
import { useCreateImageResources } from '../../../../hooks/useCreateImageResources'
import type { FormValues } from '../../types'
import * as S from './ImageFields.styled'

type Props = {
  imageFieldName: string
  imageResourceIdFieldName: string
  image?: string
}

export const ImageFields = ({ imageFieldName, imageResourceIdFieldName, image }: Props) => {
  const media = useMediaQueryContext()
  const { errors, setFieldValue } = useFormikContext<FormValues>()
  const { createImageResources, isLoading } = useCreateImageResources()

  const handleImageUpload = (files?: File[]) => {
    if (files?.[0]) {
      createImageResources({
        files,
        onSuccess: (images) => {
          setFieldValue(imageFieldName, images[0].url)
          setFieldValue(imageResourceIdFieldName, images[0].id)
        },
      })
    }
  }

  const handleImageRemove = () => {
    setFieldValue(imageFieldName, undefined)
  }

  const handleDropRejection: UploadProps['onFileDropRejected'] = (rejectedFiles) => {
    getRejectionErrors(rejectedFiles).forEach((rejection) => {
      Toasts.notify(rejection)
    })
  }

  const renderImages = () => {
    if (!image) {
      return null
    }

    return (
      <RemovableUpload onRemove={handleImageRemove}>
        <S.Container>
          <UploadedImage image={image} objectFit="contain" />
        </S.Container>
      </RemovableUpload>
    )
  }

  const renderUpload = () => {
    if (image) {
      return null
    }

    return (
      <div style={{ height: 120, position: 'relative' }}>
        <Loading isLoading={isLoading}>
          <Upload
            accept={{
              'image/*': [],
            }}
            title={
              media.tablet ? (
                <span>
                  Drop image or <u>browse</u>
                </span>
              ) : (
                'Browse'
              )
            }
            invalid={!!get(errors, imageFieldName)}
            icon={UploadIcon}
            iconSize={20}
            maxSize={10485760}
            orientation="horizontal"
            onFileDrop={handleImageUpload}
            onFileDropRejected={handleDropRejection}
          />
        </Loading>
      </div>
    )
  }

  return (
    <Subsection>
      {renderImages()}
      {renderUpload()}
      {get(errors, imageFieldName) && <InputUI.Error>{get(errors, imageFieldName)}</InputUI.Error>}
    </Subsection>
  )
}
